import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesApi } from '@/lib/api';
import type { RolesResponse, PermissionInfo } from '@/lib/api';
import type { UserRole } from 'shared/types';
import { localAuthApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Shield, Check, X, Save, RotateCcw, Lock, Crown } from 'lucide-react';

const ROLE_ORDER: UserRole[] = ['admin', 'developer', 'ba', 'member', 'viewer'];
const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  developer: 'Dev',
  ba: 'BA',
  member: 'Member',
  viewer: 'Viewer',
};
const ROLE_COLORS: Record<string, string> = {
  admin: 'text-purple-400',
  developer: 'text-blue-400',
  ba: 'text-emerald-400',
  member: 'text-slate-400',
  viewer: 'text-gray-400',
};

function groupPermissions(perms: PermissionInfo[]) {
  const groups: Record<string, PermissionInfo[]> = {};
  for (const p of perms) {
    if (!groups[p.category]) groups[p.category] = [];
    groups[p.category].push(p);
  }
  return groups;
}

export function RolePermissionsManager() {
  const queryClient = useQueryClient();
  const [dirty, setDirty] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: currentUser } = useQuery({
    queryKey: ['localUser'],
    queryFn: localAuthApi.getCurrentUser,
  });

  const { data: rolesData, isLoading } = useQuery<RolesResponse>({
    queryKey: ['roles'],
    queryFn: rolesApi.getRoles,
  });

  const saveMutation = useMutation({
    mutationFn: async (changes: Record<string, string[]>) => {
      for (const [role, perms] of Object.entries(changes)) {
        await rolesApi.updateRolePermissions(role as UserRole, perms);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['myPermissions'] });
      setDirty({});
      setError(null);
      setSuccess('Permissions saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const isAdmin = currentUser?.role === 'admin';

  if (isLoading || !rolesData) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">Loading permission matrix...</p>
      </div>
    );
  }

  // Build current state: server data merged with dirty edits
  const currentMatrix: Record<string, Set<string>> = {};
  for (const rp of rolesData.roles) {
    currentMatrix[rp.role] = new Set(
      dirty[rp.role] !== undefined ? dirty[rp.role] : rp.permissions
    );
  }

  const hasDirty = Object.keys(dirty).length > 0;
  const grouped = groupPermissions(rolesData.all_permissions);

  const togglePermission = (role: string, code: string) => {
    if (role === 'admin' || !isAdmin) return;
    const current = currentMatrix[role];
    const newPerms = new Set(current);
    if (newPerms.has(code)) {
      newPerms.delete(code);
    } else {
      newPerms.add(code);
    }
    // Find original from server
    const serverPerms = rolesData.roles.find((r) => r.role === role)?.permissions || [];
    const serverSet = new Set(serverPerms);
    const newArr = Array.from(newPerms);
    // If same as server, remove from dirty
    const isSame = newArr.length === serverSet.size && newArr.every((p) => serverSet.has(p));
    if (isSame) {
      const next = { ...dirty };
      delete next[role];
      setDirty(next);
    } else {
      setDirty({ ...dirty, [role]: newArr });
    }
  };

  const handleSave = () => {
    setError(null);
    saveMutation.mutate(dirty);
  };

  const handleReset = () => {
    setDirty({});
    setError(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Role Permission Matrix
          </h3>
          <p className="text-sm text-muted-foreground">
            {isAdmin
              ? 'Click cells to toggle permissions. Admin permissions are locked.'
              : 'Only the Admin can modify role permissions.'}
          </p>
        </div>
        {isAdmin && hasDirty && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending}>
              <Save className="h-3.5 w-3.5 mr-1" />
              {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">{error}</div>
      )}
      {success && (
        <div className="bg-emerald-500/15 text-emerald-500 text-sm p-3 rounded-md">{success}</div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground min-w-[200px] sticky left-0 bg-muted/50 z-10">
                Permission
              </th>
              {ROLE_ORDER.map((role) => (
                <th
                  key={role}
                  className={`px-3 py-2.5 text-center font-semibold min-w-[70px] ${ROLE_COLORS[role] || ''}`}
                >
                  <div className="flex flex-col items-center gap-0.5">
                    {role === 'admin' && <Crown className="h-3 w-3" />}
                    {ROLE_LABELS[role]}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([category, perms]) => (
              <Fragment key={category}>
                <tr>
                  <td
                    colSpan={ROLE_ORDER.length + 1}
                    className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground bg-muted/30"
                  >
                    {category}
                  </td>
                </tr>
                {perms.map((perm) => (
                  <tr
                    key={perm.code}
                    className="border-t border-border/50 hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-3 py-2 sticky left-0 bg-background z-10" title={perm.code}>
                      <div className="font-medium text-xs">{perm.description}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{perm.code}</div>
                    </td>
                    {ROLE_ORDER.map((role) => {
                      const has = currentMatrix[role]?.has(perm.code);
                      const isLocked = role === 'admin' || !isAdmin;
                      const isDirtyCell =
                        dirty[role] !== undefined &&
                        has !== new Set(rolesData.roles.find((r) => r.role === role)?.permissions).has(perm.code);

                      return (
                        <td
                          key={role}
                          className={`px-3 py-2 text-center ${
                            isLocked ? 'opacity-70' : 'cursor-pointer hover:bg-muted/40'
                          } ${isDirtyCell ? 'bg-amber-500/10' : ''}`}
                          onClick={() => togglePermission(role, perm.code)}
                        >
                          {isLocked && role === 'admin' ? (
                            has ? (
                              <Lock className="h-4 w-4 mx-auto text-purple-400" />
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )
                          ) : has ? (
                            <Check className="h-4 w-4 mx-auto text-emerald-500" />
                          ) : (
                            <X className="h-4 w-4 mx-auto text-muted-foreground/30" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
